import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { FiX } from 'react-icons/fi';
import { useSidebarProps } from '@/shared/layout/sidebar/hooks';
import { renderIcon } from '@/shared/layout/icon-registry';
import {
  sidebarBackdropClasses,
  sidebarBadgeClasses,
  sidebarBadgePrimaryClasses,
  sidebarBadgeWarningClasses,
  sidebarHeaderClasses,
  sidebarOpenClasses,
  sidebarSectionTitleClasses,
} from '@/shared/layout/sidebar/styles';
import type { SidebarProps } from '@/shared/layout/sidebar/types';
import { cn } from '@/shared/lib/cn';

type EnhancedNavItem = {
  key: string;
  to: string;
  disabled: boolean | undefined;
  icon: string | undefined;
  label: string;
  description: string | undefined;
  section: string;
  badge: string | number | undefined;
  badgeTone: 'primary' | 'warning' | 'danger' | 'neutral' | undefined;
  linkProps: { className: string; 'aria-current'?: 'page' };
  iconClasses: string;
  labelProps: { className: string };
};

export function Sidebar(props: SidebarProps) {
  const { rootProps, navProps, items, footerProps } = useSidebarProps(props);

  const sections: Record<string, EnhancedNavItem[]> = {};
  for (const item of items) {
    const section = item.section ?? 'Navigation';
    if (!sections[section]) sections[section] = [];
    sections[section].push(item as EnhancedNavItem);
  }

  const isMobileOpen = props.open ?? false;

  return (
    <>
      {isMobileOpen && (
        <div
          className={sidebarBackdropClasses}
          aria-hidden="true"
          onClick={props.onClose}
        />
      )}
      <aside
        {...rootProps}
        className={cn(rootProps.className, isMobileOpen && sidebarOpenClasses)}
        aria-hidden={!isMobileOpen && undefined}
      >
        {renderSidebarHeader(props.onClose)}
        {renderSidebarNav(sections, navProps, props.onClose)}
        {renderSidebarFooter(footerProps)}
      </aside>
    </>
  );
}

function renderSidebarHeader(onClose?: () => void) {
  return (
    <div className={sidebarHeaderClasses}>
      <Link
        to="/overview"
        className="flex items-center gap-2 overflow-hidden mx-auto lg:mx-0"
        title="Mana Locals Admin"
        onClick={onClose}
      >
        <span className="h-6 w-6 rounded-control border border-border bg-surface-raised flex items-center justify-center text-content font-bold text-xs shrink-0">
          M
        </span>
        <span className="text-content font-semibold tracking-tight">
          Mana Locals
        </span>
      </Link>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-content-muted hover:bg-surface-sunken hover:text-content transition-colors cursor-pointer shrink-0 lg:hidden"
          aria-label="Close menu"
        >
          <FiX className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function renderSidebarNav(
  sections: Record<string, ReadonlyArray<EnhancedNavItem>>,
  navProps: { className: string; 'aria-label': string },
  onClose?: () => void,
): ReactNode {
  return (
    <nav {...navProps}>
      {Object.entries(sections).map(([sectionTitle, sectionItems]) => (
        <div key={sectionTitle} className="flex flex-col gap-0.5">
          <span className={sidebarSectionTitleClasses}>{sectionTitle}</span>
          {renderSectionItems(sectionItems, onClose)}
        </div>
      ))}
    </nav>
  );
}

function renderSectionItems(
  items: ReadonlyArray<EnhancedNavItem>,
  onClose?: () => void,
): ReactNode {
  return items.map((item) => {
    /*
     * The icon comes from the registry, not from a `switch` here.
     *
     * The switch used to list five names and return `null` for everything else,
     * so a destination whose icon the registry DID know still rendered blank —
     * the registry was the source of truth for the name and this file was a
     * second, shorter source of truth for the glyph. Adding an icon meant editing
     * two files and remembering to, which is how the sidebar ended up with icons
     * on five rows and nothing on the rest.
     *
     * `renderIcon` resolves through the registry and returns `null` for an
     * unknown name, so the fallback is now a property of the registry rather than
     * of this call site.
     */
    const iconEl = renderIcon(item.icon, item.iconClasses);
    return (
      <Link
        key={item.to}
        to={item.to}
        aria-disabled={item.disabled}
        {...item.linkProps}
        onClick={onClose}
      >
        {iconEl}
        {item.description ? (
          <span className="flex min-w-0 flex-col leading-tight">
            <span {...item.labelProps}>{item.label}</span>
            <span className="truncate text-2xs font-normal text-content-muted/70">
              {item.description}
            </span>
          </span>
        ) : (
          <span {...item.labelProps}>{item.label}</span>
        )}
        {item.badge ? (
          <span
            className={cn(
              sidebarBadgeClasses,
              item.badgeTone === 'warning'
                ? sidebarBadgeWarningClasses
                : sidebarBadgePrimaryClasses,
            )}
          >
            {item.badge}
          </span>
        ) : null}
      </Link>
    );
  });
}

function renderSidebarFooter(footerProps: { className: string }) {
  return (
    <div {...footerProps}>
      <div className="flex flex-col items-center gap-1 px-1.5 py-1 text-center text-xs text-content-muted/75 leading-tight">
        <span className="font-semibold tracking-tight">
          &copy; {new Date().getFullYear()} Mana Locals
        </span>
        <span className="flex items-center justify-center gap-1 text-2xs text-content-muted/60">
          <span>Powered by</span>
          <a
            href="https://yeturutech.com/"
            target="_blank"
            rel="noreferrer noopener"
            className="font-semibold text-content-muted/80 underline underline-offset-2 transition-colors hover:text-content"
          >
            yeturu Tech
          </a>
        </span>
      </div>
    </div>
  );
}

export { useSidebarProps } from '@/shared/layout/sidebar/hooks';
export type { SidebarNavItem, SidebarProps } from '@/shared/layout/sidebar/types';